import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Search, Filter, Download, UserPlus, MoreVertical,
    Edit, Trash2, Eye, Shield, ShieldOff, ChevronLeft,
    ChevronRight, Users as UsersIcon, CheckCircle, XCircle, Clock,
    Loader2, AlertCircle, CheckSquare, Square, Wifi, WifiOff
} from 'lucide-react';
import api from '@/services/api';
import webSocketService from '@/services/websocket';
import toast from 'react-hot-toast';

const Users = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
    const itemsPerPage = 10;

    // WebSocket connection and event handlers
    useEffect(() => {
        // Connect to WebSocket
        const socket = webSocketService.connect();
        if (socket) {
            setIsWebSocketConnected(webSocketService.isConnected());

            // Handle connection status changes
            const handleConnect = () => {
                setIsWebSocketConnected(true);
                toast.success('Real-time updates connected', { duration: 2000 });
            };

            const handleDisconnect = () => {
                setIsWebSocketConnected(false);
                toast.error('Real-time updates disconnected', { duration: 2000 });
            };

            // Handle user events
            const handleUserCreated = (data) => {
                console.log('User created:', data);
                toast.success(`New user created: ${data.user.profile?.name || data.user.email}`);
                queryClient.invalidateQueries(['adminUsers']);
            };

            const handleUserUpdated = (data) => {
                console.log('User updated:', data);
                toast.success(`User updated: ${data.user.profile?.name || data.user.email}`);
                queryClient.invalidateQueries(['adminUsers']);
            };

            const handleUserStatusChanged = (data) => {
                console.log('User status changed:', data);
                const status = data.isActive ? 'activated' : 'deactivated';
                toast.success(`User ${status}: ${data.user.profile?.name || data.user.email}`);
                queryClient.invalidateQueries(['adminUsers']);
            };

            const handleUserDeleted = (data) => {
                console.log('User deleted:', data);
                toast.success(`User deleted: ${data.userInfo.name || data.userInfo.email}`);
                queryClient.invalidateQueries(['adminUsers']);
            };

            // Register event listeners
            webSocketService.on('connect', handleConnect);
            webSocketService.on('disconnect', handleDisconnect);
            webSocketService.onUserCreated(handleUserCreated);
            webSocketService.onUserUpdated(handleUserUpdated);
            webSocketService.onUserStatusChanged(handleUserStatusChanged);
            webSocketService.onUserDeleted(handleUserDeleted);

            // Cleanup on component unmount
            return () => {
                webSocketService.off('connect', handleConnect);
                webSocketService.off('disconnect', handleDisconnect);
                webSocketService.offUserCreated(handleUserCreated);
                webSocketService.offUserUpdated(handleUserUpdated);
                webSocketService.offUserStatusChanged(handleUserStatusChanged);
                webSocketService.offUserDeleted(handleUserDeleted);
            };
        }
    }, [queryClient]);

    // Fetch users
    const { data, isLoading } = useQuery({
        queryKey: ['adminUsers', currentPage, searchTerm, statusFilter],
        queryFn: async () => {
            const params = new URLSearchParams({
                page: currentPage,
                limit: itemsPerPage,
                search: searchTerm,
                status: statusFilter
            });
            const response = await api.get(`/admin/users?${params}`);
            return response.data;
        }
    });

    // Delete user mutation
    const deleteUserMutation = useMutation({
        mutationFn: async (userId) => {
            const response = await api.delete(`/admin/users/${userId}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['adminUsers']);
            setShowDeleteModal(false);
            setUserToDelete(null);
        }
    });

    // Toggle user status mutation
    const toggleStatusMutation = useMutation({
        mutationFn: async ({ userId, status }) => {
            const response = await api.patch(`/admin/users/${userId}/toggle-status`, { status });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['adminUsers']);
        }
    });

    // Export users
    const handleExport = async () => {
        try {
            const response = await api.get('/admin/users/export/csv', {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `users-export-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export users:', error);
        }
    };

    const handleSelectAll = () => {
        if (selectedUsers.length === data?.users?.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(data.users.map(user => user._id));
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleDeleteUser = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const getStatusBadge = (user) => {
        if (!user.isActive) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Inactive
        </span>
            );
        }
        if (user.mustChangePassword) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Password Change Required
        </span>
            );
        }
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="h-3 w-3 mr-1" />
        Active
      </span>
        );
    };

    const totalPages = Math.ceil((data?.total || 0) / itemsPerPage);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Users</h1>
                            <p className="mt-2 text-gray-600">
                                Manage user accounts and permissions
                            </p>
                        </div>
                        <Link
                            to="/admin/users/create"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
                        >
                            <UserPlus className="h-4 w-4 mr-2" />
                            Create User
                        </Link>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                            <div className="flex items-center space-x-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        placeholder="Search users..."
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    />
                                </div>
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className="p-2 text-gray-600 hover:text-gray-700 bg-white border border-gray-300 rounded-md"
                                >
                                    <Filter className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="flex items-center space-x-3">
                                {selectedUsers.length > 0 && (
                                    <span className="text-sm text-gray-600">
                    {selectedUsers.length} selected
                  </span>
                                )}
                                <button
                                    onClick={handleExport}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export
                                </button>
                            </div>
                        </div>

                        {/* Filters Panel */}
                        {showFilters && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center space-x-4">
                                    <label className="text-sm font-medium text-gray-700">Status:</label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => {
                                            setStatusFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="all">All Users</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="pending">Password Change Required</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Users Table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        checked={selectedUsers.length === data?.users?.length && data?.users?.length > 0}
                                        onChange={handleSelectAll}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    2FA
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Last Login
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center">
                                        <Loader2 className="animate-spin h-8 w-8 text-primary-600 mx-auto" />
                                    </td>
                                </tr>
                            ) : data?.users?.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                        No users found
                                    </td>
                                </tr>
                            ) : (
                                data?.users?.map((user) => (
                                    <tr key={user._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.includes(user._id)}
                                                onChange={() => handleSelectUser(user._id)}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    {user.profile?.profilePicture ? (
                                                        <img
                                                            className="h-10 w-10 rounded-full"
                                                            src={user.profile.profilePicture}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-600">
                                  {user.profile?.name?.charAt(0).toUpperCase() || 'U'}
                                </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {user.profile?.name || 'Unnamed User'}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {user.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(user)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {user.twoFactorEnabled ? (
                                                <Shield className="h-5 w-5 text-green-600" />
                                            ) : (
                                                <ShieldOff className="h-5 w-5 text-gray-400" />
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(user.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {user.lastLogin
                                                ? new Date(user.lastLogin).toLocaleDateString()
                                                : 'Never'
                                            }
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end space-x-2">
                                                <a
                                                    href={`/card/${user.cardUuid}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-gray-600 hover:text-gray-900"
                                                    title="View Public Card"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </a>
                                                <Link
                                                    to={`/admin/users/${user._id}`}
                                                    className="text-primary-600 hover:text-primary-900"
                                                    title="Edit User"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteUser(user)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Delete User"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700">
                                    Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                                    {Math.min(currentPage * itemsPerPage, data?.total || 0)} of{' '}
                                    {data?.total || 0} users
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 text-gray-600 hover:text-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft className="h-5 w-5" />
                                    </button>

                                    {[...Array(totalPages)].map((_, i) => {
                                        const page = i + 1;
                                        if (
                                            page === 1 ||
                                            page === totalPages ||
                                            (page >= currentPage - 1 && page <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={page}
                                                    onClick={() => setCurrentPage(page)}
                                                    className={`px-3 py-1 rounded ${
                                                        currentPage === page
                                                            ? 'bg-primary-600 text-white'
                                                            : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        }
                                        if (page === currentPage - 2 || page === currentPage + 2) {
                                            return <span key={page}>...</span>;
                                        }
                                        return null;
                                    })}

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 text-gray-600 hover:text-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRight className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-md w-full p-6">
                            <div className="mb-4">
                                <div className="flex items-center mb-4">
                                    <AlertCircle className="h-6 w-6 text-red-600 mr-2" />
                                    <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Are you sure you want to delete <strong>{userToDelete?.profile?.name || userToDelete?.email}</strong>?
                                    This action cannot be undone.
                                </p>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setUserToDelete(null);
                                    }}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => deleteUserMutation.mutate(userToDelete._id)}
                                    disabled={deleteUserMutation.isPending}
                                    className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                >
                                    {deleteUserMutation.isPending ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Users;